/**
 * AddVOModal Component
 * Modal for adding a new variation order
 */

import React, { useState, useRef } from 'react';
import { variationOrderAPI, projectFilesAPI } from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import { toast } from 'react-toastify';

const AddVOModal = ({ isOpen, onClose, onSuccess, projectId }) => {
  const [formData, setFormData] = useState({
    description: '',
    amount: ''
  });
  const [quotationFiles, setQuotationFiles] = useState([]);
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const quotationInputRef = useRef(null);
  const invoiceInputRef = useRef(null);

  // Handle file selection
  const handleFileSelect = (e, fileType) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf';
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB
      if (!isValidType) {
        toast.error(`${file.name}: Only images and PDFs are allowed`);
      }
      if (!isValidSize) {
        toast.error(`${file.name}: File size must be under 10MB`);
      }
      return isValidType && isValidSize;
    });

    if (fileType === 'quotation') {
      setQuotationFiles(prev => [...prev, ...validFiles]);
    } else {
      setInvoiceFiles(prev => [...prev, ...validFiles]);
    }
    e.target.value = '';
  };

  // Remove file
  const removeFile = (index, fileType) => {
    if (fileType === 'quotation') {
      setQuotationFiles(prev => prev.filter((_, i) => i !== index));
    } else {
      setInvoiceFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Upload files helper
  const uploadFiles = async (files, fileType) => {
    const uploadedFiles = [];
    for (const file of files) {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('projectId', projectId);
      formDataUpload.append('fileType', fileType);

      const uploadRes = await projectFilesAPI.upload(formDataUpload);
      if (uploadRes.data.data) {
        uploadedFiles.push({
          fileId: uploadRes.data.data.id,
          fileName: uploadRes.data.data.originalName,
          filePath: uploadRes.data.data.filePath
        });
      }
    }
    return uploadedFiles;
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSubmitting(true);
    setUploadProgress(0);

    try {
      // Upload files
      let uploadedQuotationFiles = [];
      let uploadedInvoiceFiles = [];
      const totalFiles = quotationFiles.length + invoiceFiles.length;

      if (quotationFiles.length > 0) {
        uploadedQuotationFiles = await uploadFiles(quotationFiles, 'vo_quotation');
        setUploadProgress(Math.round((quotationFiles.length / totalFiles) * 50));
      }

      if (invoiceFiles.length > 0) {
        uploadedInvoiceFiles = await uploadFiles(invoiceFiles, 'vo_invoice');
        setUploadProgress(50);
      }

      // Create the VO
      await variationOrderAPI.create(projectId, {
        description: formData.description,
        amount: parseFloat(formData.amount),
        quotationFiles: uploadedQuotationFiles,
        invoiceFiles: uploadedInvoiceFiles
      });

      setUploadProgress(100);
      toast.success('Variation order added successfully');
      onSuccess();
    } catch (err) {
      console.error('Error creating VO:', err);
      toast.error(err.message || 'Failed to create variation order');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-gray-500 bg-opacity-75 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg sm:mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Add Variation Order
            </h3>
          </div>

          {/* Content */}
          <div className="px-4 sm:px-6 py-4 overflow-y-auto flex-1">
            {/* Form Fields */}
            <div className="space-y-4">
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  disabled={submitting}
                  rows={3}
                  placeholder="Describe the variation order..."
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 text-base touch-manipulation"
                  autoFocus
                />
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount (RM) *
                </label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  disabled={submitting}
                  min="0"
                  step="0.01"
                  placeholder="Enter VO amount"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 text-base min-h-[48px] touch-manipulation"
                />
              </div>

              {/* Quotation Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quotation (Optional)
                </label>
                <div
                  onClick={() => !submitting && quotationInputRef.current?.click()}
                  className={`border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50 active:bg-purple-100 transition-colors min-h-[60px] touch-manipulation ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <svg className="w-6 h-6 mx-auto text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm text-gray-600">Upload quotation file</p>
                </div>
                <input
                  ref={quotationInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={(e) => handleFileSelect(e, 'quotation')}
                  className="hidden"
                />

                {/* Selected quotation files */}
                {quotationFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {quotationFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-purple-50 px-3 py-2 rounded-lg">
                        <span className="text-sm text-gray-700 truncate flex-1">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index, 'quotation')}
                          disabled={submitting}
                          className="text-red-500 hover:text-red-700 ml-2 p-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Invoice Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice (Optional)
                </label>
                <div
                  onClick={() => !submitting && invoiceInputRef.current?.click()}
                  className={`border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50 active:bg-purple-100 transition-colors min-h-[60px] touch-manipulation ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <svg className="w-6 h-6 mx-auto text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-sm text-gray-600">Upload invoice file</p>
                </div>
                <input
                  ref={invoiceInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={(e) => handleFileSelect(e, 'invoice')}
                  className="hidden"
                />

                {/* Selected invoice files */}
                {invoiceFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {invoiceFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-purple-50 px-3 py-2 rounded-lg">
                        <span className="text-sm text-gray-700 truncate flex-1">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index, 'invoice')}
                          disabled={submitting}
                          className="text-red-500 hover:text-red-700 ml-2 p-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Upload Progress */}
            {submitting && uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mt-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Uploading...</span>
                  <span className="font-medium">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="w-full sm:w-auto px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 active:scale-[0.98] min-h-[48px] touch-manipulation transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 active:scale-[0.98] min-h-[48px] touch-manipulation transition-all disabled:opacity-50"
            >
              {submitting ? 'Adding...' : 'Add Variation Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddVOModal;
